// HEIC/HEIF detection and conversion.
//
// ─────────────────────────────────────────────────────────────────────────────
// DISABLED: the `heic-to` package has been removed to cut the total build size
// (it bundles a large WASM-backed decoder). Image upload is turned off via
// REACT_APP_IMAGE_UPLOAD=false, so HEIC/HEIF conversion is no longer needed.
//
// This module keeps the exact same public API so every call site stays in place:
//   - `isHeicInputMime` still works (config-only, no package needed).
//   - `normalizeHeicIfNeeded` is now a pass-through: it returns the original
//     blob unchanged (converted: false) instead of decoding HEIC to JPEG.
//
// TO REVERT (re-enable HEIC conversion):
//   1. Reinstall the package:
//        npm install heic-to@^1.4.2
//   2. Replace `normalizeHeicIfNeeded` below with the "ORIGINAL IMPLEMENTATION"
//      preserved at the bottom of this file (and restore the lazy loader).
//      Nothing else needs to change.
// ─────────────────────────────────────────────────────────────────────────────

import { HEIC_INPUT_MIMES } from '../config';

export function isHeicInputMime(mime) {
  if (!mime || typeof mime !== 'string') {
    return false;
  }
  return HEIC_INPUT_MIMES.includes(mime.trim().toLowerCase());
}

/**
 * Pass-through: HEIC conversion is disabled, so the original blob is returned
 * unchanged. Kept for API compatibility with call sites.
 * @returns {Promise<{ blob: Blob, name: string, converted: boolean }>}
 */
export async function normalizeHeicIfNeeded(fileOrBlob) {
  const name = fileOrBlob.name || '';
  return { blob: fileOrBlob, name, converted: false };
}

// ─────────────────────────────────────────────────────────────────────────────
// ORIGINAL IMPLEMENTATION (re-enable on revert — see instructions above).
//
// import { ERR_IMAGE_FORMAT_UNRECOGNIZED } from './image-errors.js';
//
// // `heic-to` bundles a large WASM-backed decoder, so it is loaded on demand via
// // a dynamic import() inside normalizeHeicIfNeeded() rather than statically.
// // This keeps it out of the initial entrypoint bundle; it is only fetched the
// // first time a HEIC/HEIF file is actually processed.
//
// // Cached lazy-load of the heic-to module so it is fetched at most once.
// let _heicToPromise = null;
// function loadHeicTo() {
//   if (!_heicToPromise) {
//     _heicToPromise = import('heic-to');
//   }
//   return _heicToPromise;
// }
//
// function looksLikeHeic(fileOrBlob) {
//   const mime = (fileOrBlob.type || '').trim().toLowerCase();
//   const name = fileOrBlob.name || '';
//   if (HEIC_INPUT_MIMES.includes(mime)) {
//     return true;
//   }
//   return /\.(heic|heif)$/i.test(name);
// }
//
// /**
//  * Converts HEIC/HEIF to JPEG when needed; otherwise returns the original blob.
//  * @returns {Promise<{ blob: Blob, name: string, converted: boolean }>}
//  */
// export async function normalizeHeicIfNeeded(fileOrBlob) {
//   const name = fileOrBlob.name || '';
//   const blob = fileOrBlob;
//
//   if (!looksLikeHeic(fileOrBlob)) {
//     return { blob, name, converted: false };
//   }
//
//   const fileForHeic = fileOrBlob instanceof File
//     ? fileOrBlob
//     : new File([blob], name || 'image.heic', { type: fileOrBlob.type || 'image/heic' });
//
//   const { heicTo, isHeic } = await loadHeicTo();
//
//   const detected = await isHeic(fileForHeic).catch(() => false);
//   if (!detected) {
//     return { blob, name, converted: false };
//   }
//
//   try {
//     const jpegBlob = await heicTo({ blob: fileForHeic, type: 'image/jpeg', quality: 0.92 });
//     const base = name.replace(/\.[^.]+$/i, '') || 'image';
//     return { blob: jpegBlob, name: base + '.jpg', converted: true };
//   } catch (e) {
//     throw new Error(ERR_IMAGE_FORMAT_UNRECOGNIZED);
//   }
// }
